import React, { type FC, useEffect, type PropsWithChildren } from 'react';

import clsx from 'clsx';
import { isBrowser } from '@lucid/utils';

import Box from '../box/box';
import type { Lucid } from '../../types/types';
import LoadingIndicator from '../loading-indicator/loading-indicator';

export interface WistiaVideo {
  /** The Wistia email function. */
  email: (email: string) => void;
  /** The Wistia bind function. */
  bind: (str: string, funct: (type: string, userEmail: string) => void) => void;
  /** The Wistia remove function. */
  remove: () => void;
}

export interface WistiaEmbedProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** The hashed ID from Wistia. */
  hashedId: string;
  /** The Wistia options. */
  embedOptions?: Lucid.Wistia.EmbedOptions;
}

const WistiaEmbed: FC<PropsWithChildren<WistiaEmbedProps>> = ({ className, hashedId, embedOptions = {}, ...rest }) => {
  if (isBrowser()) {
    window._wq = window._wq || [];

    window._wq.push({
      id: hashedId,
      options: embedOptions,
    });
  }

  useEffect(() => {
    let handle: WistiaVideo;

    if (isBrowser()) {
      if (!document.getElementById('wistia_embed_script')) {
        const wistiaScript = document.createElement('script');
        wistiaScript.id = 'wistia_embed_script';
        wistiaScript.type = 'text/javascript';
        wistiaScript.src = 'https://fast.wistia.com/assets/external/E-v1.js';
        wistiaScript.async = true;
        wistiaScript.dataset.osano = 'ESSENTIAL';
        document.head.appendChild(wistiaScript);
      }

      window._wq = window._wq || [];

      window._wq.push({
        id: hashedId,
        onEmbedded: (video: WistiaVideo) => {
          handle = video;
          const email = window.Wistia.localStorage('golden-ticket');

          if (email) {
            handle.email(email);
          }

          handle.bind('conversion', (type: string, userEmail: string) => {
            if (/^(pre|mid|post)-roll-email$/.test(type)) {
              window.Wistia.localStorage('golden-ticket', userEmail);
              const videos = window.Wistia.api.all();
              const { length } = videos;

              for (let i = 0; i < length; i += 1) {
                videos[i].email(userEmail);
              }
            }
          });
        },
      });
    }

    return () => {
      if (handle) {
        handle.remove();
      }
    };
  }, [hashedId]);

  return (
    <Box
      {...rest}
      css={{
        label: 'wistiaEmbed',
        padding: '56.25% 0 0 0',
        position: 'relative',
      }}
      // eslint-disable-next-line @stylistic/jsx-sort-props
      className={clsx('wistia_responsive_padding', className)}
    >
      <Box
        className="wistia_responsive_wrapper"
        css={{
          position: 'absolute',
          height: '100%',
          left: 0,
          top: 0,
          width: '100%',
        }}
      >
        <Box
          css={theme => ({
            height: '100%',
            width: '100%',
            display: 'flex',
            'button[type="submit"]:focus': {
              backgroundColor: `${theme.colors.coolGrey[100]} !important`,
              outline: `3px solid ${theme.colors.blue[40]} !important`,
            },
          })}
          // eslint-disable-next-line @stylistic/jsx-sort-props
          className={clsx('wistia_embed', `wistia_async_${hashedId}`, {
            videoFoam: true,
          })}
        >
          {/* This will be removed on load of the wistia video */}
          <LoadingIndicator />
        </Box>
      </Box>
    </Box>
  );
};

export { WistiaEmbed };
export default WistiaEmbed;
